/**
 * FichaClienteModal — Auditoría 2026-08-27, CAMBIO 7. Ficha del cliente:
 * datos, próxima acción de seguimiento y presupuestos asociados con su
 * estado (solo lectura — no modifica ningún presupuesto).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { X, Loader2, FileText, Building2, CalendarClock, Send } from "lucide-react";
import { API } from "../contexts/AuthContext";
import { formatPYGCompact, formatDate } from "../utils/format";
import { toast } from "sonner";

const STATUS_BADGE = {
  cotizacion: "bg-amber-100 text-amber-700", enviado: "bg-blue-100 text-blue-700",
  aprobado: "bg-emerald-100 text-emerald-700", rechazado: "bg-red-100 text-red-700",
};

export default function FichaClienteModal({ cliente, onClose, onSaved }) {
  const [presupuestos, setPresupuestos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fecha, setFecha] = useState(cliente.proxima_accion?.fecha || "");
  const [descripcion, setDescripcion] = useState(cliente.proxima_accion?.descripcion || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/listas/clientes/${cliente._id}/presupuestos`);
      setPresupuestos(data || []);
    } catch { toast.error("Error al cargar los presupuestos del cliente"); }
    finally { setLoading(false); }
  }, [cliente._id]);
  useEffect(() => { load(); }, [load]);

  const guardarProximaAccion = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/listas/clientes/${cliente._id}/proxima-accion`, { fecha, descripcion });
      setFecha(data.proxima_accion?.fecha || "");
      setDescripcion(data.proxima_accion?.descripcion || "");
      setDirty(true);
      toast.success("Próxima acción guardada");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const handleClose = () => { onClose?.(); if (dirty) onSaved?.(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="ficha-cliente-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto flex flex-col">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <Building2 size={16} className="text-[#2E6DA4]" /> Ficha — {cliente.cliente}
          </h2>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="ficha-cliente-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5 flex-1">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><p className="text-xs text-gray-400">Origen</p><p className="text-gray-800">{cliente.origen || "—"}</p></div>
            <div><p className="text-xs text-gray-400">Responsable</p><p className="text-gray-800">{cliente.responsable || "—"}</p></div>
            <div><p className="text-xs text-gray-400">RUC/CI</p><p className="text-gray-800">{cliente.ruc_ci || "—"}</p></div>
            <div><p className="text-xs text-gray-400">Email</p><p className="text-gray-800">{cliente.email || "—"}</p></div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="text-xs font-bold text-gray-500 uppercase flex items-center gap-1.5"><CalendarClock size={13} /> Próxima acción</p>
            <div className="flex gap-2 flex-wrap">
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)}
                className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm" data-testid="ficha-cliente-proxima-fecha" />
              <input value={descripcion} onChange={(e) => setDescripcion(e.target.value)} placeholder="Descripción de la acción..."
                className="flex-1 min-w-[180px] border border-gray-200 rounded-lg px-3 py-1.5 text-sm" data-testid="ficha-cliente-proxima-descripcion" />
              <button onClick={guardarProximaAccion} disabled={saving}
                className="px-3 py-1.5 rounded-lg bg-[#2E6DA4] text-white text-xs font-semibold disabled:opacity-50 flex items-center gap-1"
                data-testid="ficha-cliente-proxima-guardar">
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Guardar
              </button>
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1.5"><FileText size={13} /> Presupuestos asociados</p>
            {loading ? (
              <div className="text-center py-6"><Loader2 className="animate-spin mx-auto text-gray-300" /></div>
            ) : presupuestos.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-4" data-testid="ficha-cliente-sin-presupuestos">Sin presupuestos asociados todavía</p>
            ) : (
              <div className="border border-gray-100 rounded-xl divide-y divide-gray-50">
                {presupuestos.map((p) => (
                  <div key={p._id} className="flex items-center justify-between px-3 py-2.5 text-sm" data-testid={`ficha-cliente-presupuesto-${p._id}`}>
                    <div>
                      <p className="font-semibold text-gray-800">{p.number}</p>
                      <p className="text-xs text-gray-400">{p.obra || "—"} · {formatDate(p.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-700 font-medium">{formatPYGCompact(p.total)}</span>
                      <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${STATUS_BADGE[p.status] || "bg-gray-100 text-gray-500"}`}>{p.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
